import {
  bySignal,
  fmtNum,
  matches,
  siteSignals,
  todayView,
  type SignalKind,
  type Site,
  t,
} from '@procumi/shared';
import { useMemo, useState } from 'react';
import { SiteDialog } from '../components/SiteDialog';
import { ErrorBox, Loading } from '../components/StateBox';
import { useAsync } from '../hooks/useAsync';
import type { Toast } from '../hooks/useToast';
import { api } from '../lib/api';
import type { Route } from '../routes';
import { siteTerm } from '../lib/terms';

/**
 * Lista de șantiere, cu cele care cer ceva azi în față.
 *
 * Semnalul se calculează din aceleași date ca panoul de control și cu aceeași
 * logică din `shared`, în browser. De asta pagina aduce și `/api/today`: ruta
 * de șantiere întoarce doar numărătoare, iar din numărătoare nu se poate ști
 * dacă un termen a trecut — „azi" e ziua utilizatorului, nu a serverului.
 *
 * Căutarea e în browser, ca și filtrul: șantierele nu sunt paginate, fiindcă o
 * firmă are zeci, nu mii, iar paginarea lor ar strica tocmai sortarea după
 * urgență, care se calculează aici.
 */

/**
 * Semnalul, cu culorile plăcuțelor de pe panou: albastru pentru ce poți
 * termina, roșu pentru termen depășit, chihlimbar pentru ce aștepți. Ofertele
 * noi rămân fără culoare — sunt informare, nu cer nimic.
 */
const SEMNAL: Record<SignalKind, { pill: string; text: (n: number) => string }> = {
  decide: { pill: 'act', text: (n) => t('{0} de decis', n) },
  late: { pill: 'late', text: (n) => t('{0} cu termen depășit', n) },
  silent: { pill: 'wait', text: (n) => t('{0} fără răspuns', n) },
  due: { pill: 'wait', text: (n) => t('{0} cu termen aproape', n) },
  fresh: { pill: 'off', text: (n) => (n === 1 ? t('o ofertă nouă') : t('{0} oferte noi', n)) },
};

/** Unde caută caseta: nume, localitate și beneficiar. */
const undeCaut = (s: Site): string => `${s.name} ${s.locality} ${s.client}`;

export function SitesPage({ go, toast }: { go: (r: Route) => void; toast: Toast }) {
  const { data, error, loading, reload } = useAsync(
    () => Promise.all([api.sites({ includeClosed: true }), api.today()]),
    [],
    'sites+today',
  );
  const [dialog, setDialog] = useState<{ site?: Site } | null>(null);
  const [inchise, setInchise] = useState(false);
  const [cauta, setCauta] = useState('');

  const signals = useMemo(() => siteSignals(todayView(data ? data[1] : [])), [data]);

  const all = data ? data[0] : [];
  const deschise = all.filter((s) => !s.closedAt);
  const closed = all.filter((s) => s.closedAt);
  const scope = inchise ? closed : deschise;

  const rows = useMemo(() => {
    const gasite = scope.filter((s) => matches(undeCaut(s), cauta));
    // Sortarea după urgență are sens doar la cele deschise: un șantier închis
    // nu mai cere nimic, deci rămâne în ordinea de la server.
    return inchise ? gasite : bySignal(gasite, signals);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, cauta, inchise, signals]);

  const filtrat = cauta.trim().length > 0;

  return (
    <>
      <div className="head">
        <div>
          <h1>{t('Șantiere')}</h1>
          <p>
            {t(
              'Fiecare cerere de ofertă ține de un șantier, ca să vezi într-un loc tot ce s-a cerut și tot ce s-a cumpărat pentru el. Primele sunt cele care cer ceva azi.',
            )}
          </p>
        </div>
        <button className="btn primary" onClick={() => setDialog({})}>
          {t('Șantier nou')}
        </button>
      </div>

      {loading ? <Loading /> : null}
      {!loading && error ? <ErrorBox error={error} onRetry={reload} /> : null}

      {!loading && !error ? (
        <>
          {closed.length ? (
            <div className="filters">
              <button aria-pressed={!inchise} onClick={() => setInchise(false)}>
                {t('Deschise')} <span className="muted">{deschise.length}</span>
              </button>
              <button aria-pressed={inchise} onClick={() => setInchise(true)}>
                {t('Închise')} <span className="muted">{closed.length}</span>
              </button>
            </div>
          ) : null}

          {all.length > 1 ? (
            <div className="list-tools">
              <input
                className="inp"
                placeholder={t('Caută după nume, localitate sau beneficiar…')}
                value={cauta}
                onChange={(e) => setCauta(e.target.value)}
                aria-label={t('Caută un șantier')}
              />
              {filtrat ? <span className="muted">{t('{0} din {1}', rows.length, scope.length)}</span> : null}
            </div>
          ) : null}

          <div className="sheet rfqs">
            {rows.length ? (
              rows.map((s) => {
                const semnal = s.closedAt ? undefined : signals.get(s.id);
                const cum = semnal ? SEMNAL[semnal.kind] : null;
                return (
                  <button className="rfq-row" key={s.id} onClick={() => go({ view: 'site', id: s.id })}>
                    <span>
                      <strong>{s.name}</strong>
                      <span className="sub">
                        {s.locality || t('fără localitate')}
                        {s.client ? ` · ${s.client}` : ''}
                        {s.closedAt ? (
                          <>
                            {' '}
                            <span className="pill off">{t('Închis')}</span>
                          </>
                        ) : null}
                      </span>
                    </span>
                    <span className="count">
                      {s.bought ? (
                        <>
                          <b style={{ color: 'var(--ink)' }}>{fmtNum(Math.round(s.bought))}</b>
                          <br />
                          {t('lei cumpărat')}
                        </>
                      ) : (
                        <span className="muted">{s.rfqCount ? t('nimic comandat') : t('nicio cerere')}</span>
                      )}
                    </span>
                    <span className="count">
                      {semnal && cum ? (
                        <span className={`pill ${cum.pill}`}>{cum.text(semnal.n)}</span>
                      ) : (
                        <span className="muted">
                          {s.closedAt
                            ? t(
                                '{0} {1} în istoric',
                                s.rfqCount,
                                s.rfqCount === 1 ? t('cerere') : t('cereri'),
                              )
                            : s.openRfqCount
                              ? t('nimic azi')
                              : t('nicio cerere deschisă')}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="empty">
                {filtrat ? (
                  <>
                    {t('Niciun șantier nu se potrivește.')}
                    <br />
                    <button className="btn small" style={{ marginTop: 12 }} onClick={() => setCauta('')}>
                      {t('Șterge căutarea')}
                    </button>
                  </>
                ) : inchise ? (
                  t('Niciun șantier închis.')
                ) : (
                  t(
                    'Nu ai niciun șantier deschis. Adaugă primul — de exemplu {0}.',
                    siteTerm().id === 'santier' ? t('„Creșă Pucioasa"') : t('„Sediul central"'),
                  )
                )}
              </div>
            )}
          </div>
        </>
      ) : null}

      {dialog ? (
        <SiteDialog
          site={dialog.site}
          toast={toast}
          onSaved={(saved) => {
            reload();
            if (!dialog.site) go({ view: 'site', id: saved.id });
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </>
  );
}
